import React, { useEffect, useState } from "react";
import { DraftState, Pick, Transaction } from "../objects/types";
import { getDraftState } from "../repository/api";

interface DraftBoardProps {
  year: number;
}

const DraftBoard: React.FC<DraftBoardProps> = ({ year }) => {
  const [draftState, setDraftState] = useState<DraftState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getDraftState(year);
        setDraftState(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load draft");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [year]);

  if (loading) {
    return <div className="loading">Loading draft board...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  if (!draftState) {
    return <div className="empty-state">No draft data available.</div>;
  }

  const { picks, trades } = draftState;

  const picksByRound: Record<number, Pick[]> = {};
  picks.forEach((pick) => {
    const round = pick.round;
    if (!picksByRound[round]) {
      picksByRound[round] = [];
    }
    picksByRound[round].push(pick);
  });

  const rounds = Object.keys(picksByRound)
    .map(Number)
    .sort((a, b) => a - b);

  return (
    <div>
      <h2>{year} Draft Board</h2>

      {trades.length > 0 && (
        <div className="card" style={{ marginBottom: "1.5rem" }}>
          <h3>Pick Trades</h3>
          <ul style={{ margin: 0, paddingLeft: "1.25rem" }}>
            {trades.map((trade) => (
              <li key={trade.uuid} style={{ marginBottom: "0.5rem" }}>
                {formatTrade(trade)}
              </li>
            ))}
          </ul>
        </div>
      )}

      {picks.length === 0 ? (
        <div className="empty-state">
          No picks created yet for {year}. Create picks in the Admin panel.
        </div>
      ) : (
        rounds.map((round) => (
          <div key={round} className="card" style={{ marginBottom: "1rem" }}>
            <h3>Round {round}</h3>
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Pick #</th>
                    <th>Original Team</th>
                    <th>Current Owner</th>
                    <th>Type</th>
                    <th>Result</th>
                  </tr>
                </thead>
                <tbody>
                  {picksByRound[round]
                    .sort((a, b) => (a.pick_number || 999) - (b.pick_number || 999))
                    .map((pick) => (
                      <tr key={pick.uuid}>
                        <td>{pick.pick_number || "TBD"}</td>
                        <td>{pick.natural_team?.name || "Unknown"}</td>
                        <td>
                          {pick.actual_team?.name ||
                            pick.natural_team?.name ||
                            "Unknown"}
                          {pick.actual_team_uuid &&
                            pick.actual_team_uuid !== pick.natural_team_uuid && (
                              <span
                                style={{
                                  color: "var(--color-warning)",
                                  marginLeft: "0.25rem",
                                }}
                              >
                                (traded)
                              </span>
                            )}
                        </td>
                        <td>{pick.is_kp_pick ? "KP" : "Draft"}</td>
                        <td>{pick.pick_result_player_uuid || "-"}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        ))
      )}
    </div>
  );
};

function formatTrade(trade: Transaction): string {
  const details = trade.details as Record<string, string> | undefined;
  if (details?.description) {
    return details.description;
  }
  return `Trade recorded on ${new Date(trade.created_at).toLocaleDateString()}`;
}

export default DraftBoard;
